/**
 * VA-27852 Goal B2.1 — SHADOW-MODE speech-onset detector.
 *
 * MEASUREMENT ONLY. This module can observe that an interruption *would have*
 * happened. It can never cause one: it performs no I/O, holds no timers, no
 * listeners and no references to playback or the websocket, and its only output
 * is a state-transition event plus counters. B2.2 (wiring a confirmation to
 * `stopAssistantPlaybackNow()` and the backend interrupt) is deliberately NOT
 * implemented here.
 *
 * WHY IT LIVES IN ITS OWN MODULE. `interview-meeting-context.tsx` is a large
 * React context whose audio handler cannot be exercised without mounting the
 * whole interview. Keeping the state machine pure — same reason `audio-frame.ts`
 * is separate — means the part that decides "is this speech?" is testable
 * directly, frame by frame, rather than through a mock audio graph.
 *
 * WHERE THE NUMBERS COME FROM, AND WHERE THEY DO NOT.
 *
 * The historical B2 detector used MIN_FRAMES = 6 at ~2.67 ms/frame — about
 * **16 ms** of raw RMS. That is deliberately NOT inherited. This project already
 * runs a tuned speech-onset detector on the backend: Silero neural VAD with
 * ONSET_MIN_FRAMES = 3 over 512-sample / 32 ms windows, i.e. **96 ms of
 * neural-classified speech**, and it applies that bar to the far less disruptive
 * decision of merely opening a candidate turn. Asking for six times *less*
 * evidence, from a much weaker classifier, to cut the interviewer off mid-word
 * is not a defensible starting point.
 *
 * So the onset window starts at the 96 ms scale. That is a *reasoned* starting
 * point, not a calibrated one — and the RMS floor in particular is PROVISIONAL
 * (see DEFAULT_BARGE_IN_PARAMS). Establishing the real values against recorded
 * cough / keyboard / chair / speakerphone audio is precisely what this shadow
 * increment exists to make possible.
 */
export const DEFAULT_BARGE_IN_PARAMS = {
    rmsFloor: 0.02,
    onsetMs: 96,
    abandonMs: 64,
    maxOnsetWindowMs: 500,
};
export class BargeInShadowDetector {
    constructor(params = DEFAULT_BARGE_IN_PARAMS) {
        this.state = 'idle';
        this.aboveMs = 0;
        this.belowMs = 0;
        this.windowMs = 0;
        this.peak = 0;
        this.lastConfirmMs = 0;
        this.counters = {
            armed: 0,
            onsetStarted: 0,
            onsetAbandoned: 0,
            wouldHaveConfirmed: 0,
        };
        this.params = Object.assign({}, params);
    }
    /**
     * VA-28665: replace the thresholds (admin-tuned values arriving in `session_create`).
     * Progress made under the old values is dropped — half an onset judged by one rule and
     * finished by another is not evidence of anything.
     */
    setParams(params) {
        this.params = Object.assign({}, params);
        this.resetProgress();
    }
    getParams() {
        return Object.assign({}, this.params);
    }
    /**
     * Feed one microphone frame.
     *
     * `frameMs` is passed in rather than assumed, because it is a function of the
     * AudioContext's ACTUAL sample rate (`samples.length / ctx.sampleRate`). The
     * historical detector hard-coded a frame count that silently encoded 48 kHz;
     * a 44.1 kHz context would have shifted its real window by ~9% with no sign.
     *
     * Returns the transition that occurred, or null. Returning null on the
     * overwhelming majority of frames is what keeps this free of per-frame
     * telemetry — the caller only has something to report when this is non-null.
     */
    observe(rms, frameMs, assistantOutputActive) {
        // Not armed: the assistant is not producing output, so nothing the
        // microphone hears can be a barge-in. Reset rather than accumulate.
        if (!assistantOutputActive) {
            if (this.state !== 'idle') {
                this.resetProgress();
                this.state = 'idle';
            }
            return null;
        }
        if (this.state === 'idle') {
            this.state = 'armed';
            this.counters.armed += 1;
            this.resetProgress();
            return 'armed';
        }
        // COOLDOWN exists because `stopAssistantPlaybackNow()` deliberately does NOT
        // clear `assistantSpeakingRef` — the microphone gate must stay closed until
        // the backend's `audio_end`. So after a (future) real cut, this detector is
        // still armed and would happily confirm again on the same utterance. In
        // shadow mode we hold here until playback genuinely ends, which is what
        // stops one barge-in being counted several times.
        if (this.state === 'cooldown') {
            return null;
        }
        if (rms > this.peak)
            this.peak = rms;
        const above = rms >= this.params.rmsFloor;
        if (this.state === 'armed') {
            if (!above)
                return null;
            this.state = 'onset';
            this.aboveMs = frameMs;
            this.belowMs = 0;
            this.windowMs = frameMs;
            this.counters.onsetStarted += 1;
            return 'onset_started';
        }
        // state === 'onset'
        this.windowMs += frameMs;
        if (above) {
            this.aboveMs += frameMs;
            this.belowMs = 0;
            if (this.aboveMs >= this.params.onsetMs) {
                this.lastConfirmMs = this.aboveMs;
                this.counters.wouldHaveConfirmed += 1;
                this.state = 'cooldown';
                return 'would_have_confirmed';
            }
        }
        else {
            // A dip COSTS the accumulated evidence. Without this, alternating
            // above/below frames accumulate `aboveMs` indefinitely and a stream of
            // isolated blips — a keyboard, a flapping noise gate — reaches the onset
            // window and confirms. Speech onset is continuous; intermittent energy is
            // exactly what must not confirm. `abandonMs` still decides when to give up
            // on the attempt entirely, so a brief dip keeps us in `onset` and merely
            // restarts the evidence rather than ending the attempt.
            this.aboveMs = 0;
            this.belowMs += frameMs;
            if (this.belowMs >= this.params.abandonMs) {
                this.counters.onsetAbandoned += 1;
                this.state = 'armed';
                this.resetProgress(/* keepPeak */ true);
                return 'onset_abandoned';
            }
        }
        // A transient that keeps flickering above/below must not creep to a
        // confirmation over seconds. Real speech onset is continuous.
        if (this.windowMs >= this.params.maxOnsetWindowMs) {
            this.counters.onsetAbandoned += 1;
            this.state = 'armed';
            this.resetProgress(/* keepPeak */ true);
            return 'onset_abandoned';
        }
        return null;
    }
    /**
     * Drop all progress. Called on `audio_end`, on microphone re-acquire /
     * device change, and on teardown — a floor and a peak learned on one device
     * mean nothing on another, and evidence from a finished turn must never carry
     * into the next one.
     *
     * Counters are cumulative and deliberately survive: they are the measurement.
     */
    reset() {
        this.resetProgress();
        this.state = 'idle';
    }
    snapshot() {
        return {
            state: this.state,
            counters: Object.assign({}, this.counters),
            peakRmsWhileArmed: this.peak,
            lastConfirmMs: this.lastConfirmMs,
        };
    }
    resetProgress(keepPeak = false) {
        this.aboveMs = 0;
        this.belowMs = 0;
        this.windowMs = 0;
        if (!keepPeak)
            this.peak = 0;
    }
}
/**
 * Root-mean-square of one frame, normalised to 0..1 for Float32 PCM.
 *
 * Deliberately a plain loop over the frame the handler ALREADY has: no second
 * worklet, no second capture path, no AnalyserNode, no new AudioContext. While
 * the assistant is speaking these samples are currently discarded outright, so
 * this reuses work that is otherwise thrown away.
 */
export const frameRms = (samples) => {
    if (!samples || samples.length === 0)
        return 0;
    let sum = 0;
    for (let i = 0; i < samples.length; i += 1) {
        const s = samples[i];
        sum += s * s;
    }
    return Math.sqrt(sum / samples.length);
};
